import type { Locale } from "@dteba/shared";

/**
 * UI chrome only — every word of page *content* comes from the API. Indonesian
 * runs 15–25% longer, so these strings are what the layout is stress-tested
 * against (brief §5, requirement 4).
 */
export const dictionary = {
  en: {
    "nav.villa": "The Villa",
    "nav.story": "The Story",
    "nav.experiences": "Experiences",
    "nav.gallery": "Gallery",
    "nav.contact": "Contact",
    "nav.faq": "FAQ",
    "nav.menu": "Menu",
    "nav.close": "Close menu",
    "nav.skip": "Skip to content",

    "cta.book": "Book your stay",
    "cta.readStory": "Read the full story",
    "cta.openMaps": "Open in Maps",
    "cta.loadMap": "Load interactive map",
    "cta.whatsapp": "Message on WhatsApp",
    "cta.backHome": "Back to the villa",
    "cta.contact": "Contact us",
    "cta.sendMessage": "Send message",

    "booking.title": "Request your dates",
    "booking.lead": "We reply within one day, usually the same morning. No payment is taken on this page.",
    "booking.eyebrow": "Booking",
    "booking.dates": "Dates",
    "booking.checkIn": "Check-in",
    "booking.checkOut": "Check-out",
    "booking.selectDates": "Select your dates",
    "booking.nights": "{count} nights · minimum stay {min} nights",
    "booking.oneNight": "1 night · minimum stay {min} nights",
    "booking.guests": "Guests",
    "booking.fullName": "Full name",
    "booking.email": "Email",
    "booking.phone": "Phone",
    "booking.notes": "Anything we should know?",
    "booking.notesPlaceholder": "Arrival time, dietary notes, airport pickup…",
    "booking.namePlaceholder": "Your full name",
    "booking.emailPlaceholder": "you@email.com",
    "booking.phonePlaceholder": "812 3456 7890",
    "booking.accept": "I've read the house rules, including the heritage etiquette for the merajan.",
    "booking.houseRules": "house rules",
    "booking.submit": "Send booking request",
    "booking.submitting": "Sending",
    "booking.disclaimer": "Sending this doesn't reserve the dates. We confirm by reply.",
    "booking.closed": "We're not taking new dates right now — please message us on WhatsApp.",
    "booking.reassurance1": "No prepayment.",
    "booking.reassurance1Rest": "Confirm by WhatsApp first.",
    "booking.reassurance2": "Free cancellation",
    "booking.reassurance2Rest": "up to 14 days before arrival.",
    "booking.reassurance3": "You reach the family",
    "booking.reassurance3Rest": "— not an agency.",
    "booking.prefer": "Prefer to message?",
    "booking.rates": "Rates",
    "booking.unavailable": "Unavailable",

    "thanks.title": "Your request is with the family",
    "thanks.lead": "We'll reply to {email} within one day — usually the same morning, Bali time.",
    "thanks.reference": "Reference {reference} · {dates} · {guests} guests",
    "thanks.readStory": "Read the story while you wait",

    "contact.eyebrow": "Contact",
    "contact.title": "Ask us anything",
    "contact.lead": "For dates and availability, the booking form is faster. For everything else, here we are.",
    "contact.name": "Name",
    "contact.namePlaceholder": "Your name",
    "contact.message": "Message",
    "contact.messagePlaceholder": "How can we help?",
    "contact.whatsapp": "WhatsApp",
    "contact.email": "Email",
    "contact.address": "Address",
    "contact.sent": "Message sent — we'll be in touch.",

    "faq.eyebrow": "FAQ",
    "faq.title": "Questions guests ask",
    "faq.stillUnsure": "Still unsure?",
    "faq.sendMessage": "Send us a message",
    "faq.human": "— someone in the family answers, not a bot.",

    "gallery.all": "All",
    "gallery.counter": "{index} / {total}",
    "gallery.open": "Open photograph",

    "notFound.eyebrow": "404",
    "notFound.title": "This path doesn't lead anywhere",
    "notFound.lead": "Not every wrong turn in a village is a mistake — but this one is. Let's get you back.",

    "footer.visit": "Visit",
    "footer.contact": "Contact",
    "footer.legal": "Privacy · Terms",

    "state.loading": "Loading…",
    "state.errorTitle": "We couldn't load this",
    "state.errorBody": "The connection dropped somewhere between here and Bali. Try again in a moment.",
    "state.retry": "Try again",
    "state.empty": "Nothing here yet.",
    "state.mapFacade": "Map facade — no third-party script loads until this is clicked.",

    "lang.label": "Language",
  },

  id: {
    "nav.villa": "Vila",
    "nav.story": "Cerita",
    "nav.experiences": "Pengalaman",
    "nav.gallery": "Galeri",
    "nav.contact": "Kontak",
    "nav.faq": "Tanya Jawab",
    "nav.menu": "Menu",
    "nav.close": "Tutup menu",
    "nav.skip": "Lompat ke konten",

    "cta.book": "Pesan sekarang",
    "cta.readStory": "Baca cerita selengkapnya",
    "cta.openMaps": "Buka di Peta",
    "cta.loadMap": "Muat peta interaktif",
    "cta.whatsapp": "Kirim pesan WhatsApp",
    "cta.backHome": "Kembali ke villa",
    "cta.contact": "Hubungi kami",
    "cta.sendMessage": "Kirim pesan",

    "booking.title": "Ajukan tanggal menginap Anda",
    "booking.lead": "Kami membalas dalam satu hari, biasanya pagi yang sama. Tidak ada pembayaran di halaman ini.",
    "booking.eyebrow": "Pemesanan",
    "booking.dates": "Tanggal",
    "booking.checkIn": "Check-in",
    "booking.checkOut": "Check-out",
    "booking.selectDates": "Pilih tanggal Anda",
    "booking.nights": "{count} malam · menginap minimal {min} malam",
    "booking.oneNight": "1 malam · menginap minimal {min} malam",
    "booking.guests": "Tamu",
    "booking.fullName": "Nama lengkap",
    "booking.email": "Email",
    "booking.phone": "Telepon",
    "booking.notes": "Ada yang perlu kami ketahui?",
    "booking.notesPlaceholder": "Jam kedatangan, catatan makanan, penjemputan bandara…",
    "booking.namePlaceholder": "Nama lengkap Anda",
    "booking.emailPlaceholder": "anda@email.com",
    "booking.phonePlaceholder": "812 3456 7890",
    "booking.accept": "Saya telah membaca aturan rumah, termasuk etika warisan untuk merajan.",
    "booking.houseRules": "aturan rumah",
    "booking.submit": "Kirim permintaan pemesanan",
    "booking.submitting": "Mengirim",
    "booking.disclaimer": "Mengirim ini belum memesan tanggalnya. Kami konfirmasi lewat balasan.",
    "booking.closed": "Kami sedang tidak menerima tanggal baru — silakan hubungi kami di WhatsApp.",
    "booking.reassurance1": "Tanpa pembayaran di muka.",
    "booking.reassurance1Rest": "Konfirmasi lewat WhatsApp lebih dulu.",
    "booking.reassurance2": "Pembatalan gratis",
    "booking.reassurance2Rest": "hingga 14 hari sebelum kedatangan.",
    "booking.reassurance3": "Anda menghubungi keluarganya",
    "booking.reassurance3Rest": "— bukan agen.",
    "booking.prefer": "Lebih suka berkirim pesan?",
    "booking.rates": "Tarif",
    "booking.unavailable": "Tidak tersedia",

    "thanks.title": "Permintaan Anda sudah kami terima",
    "thanks.lead": "Kami akan membalas ke {email} dalam satu hari — biasanya pagi yang sama, waktu Bali.",
    "thanks.reference": "Referensi {reference} · {dates} · {guests} tamu",
    "thanks.readStory": "Baca ceritanya sambil menunggu",

    "contact.eyebrow": "Kontak",
    "contact.title": "Tanyakan apa saja",
    "contact.lead": "Untuk tanggal dan ketersediaan, formulir pemesanan lebih cepat. Untuk lainnya, kami di sini.",
    "contact.name": "Nama",
    "contact.namePlaceholder": "Nama Anda",
    "contact.message": "Pesan",
    "contact.messagePlaceholder": "Ada yang bisa kami bantu?",
    "contact.whatsapp": "WhatsApp",
    "contact.email": "Email",
    "contact.address": "Alamat",
    "contact.sent": "Pesan terkirim — kami akan menghubungi Anda.",

    "faq.eyebrow": "Tanya Jawab",
    "faq.title": "Pertanyaan yang sering diajukan tamu",
    "faq.stillUnsure": "Masih ragu?",
    "faq.sendMessage": "Kirim pesan kepada kami",
    "faq.human": "— yang menjawab anggota keluarga, bukan bot.",

    "gallery.all": "Semua",
    "gallery.counter": "{index} / {total}",
    "gallery.open": "Buka foto",

    "notFound.eyebrow": "404",
    "notFound.title": "Jalan ini tidak menuju ke mana-mana",
    "notFound.lead": "Tidak semua salah belok di desa adalah kesalahan — tetapi yang ini iya. Mari kembali.",

    "footer.visit": "Kunjungi",
    "footer.contact": "Kontak",
    "footer.legal": "Privasi · Ketentuan",

    "state.loading": "Memuat…",
    "state.errorTitle": "Kami tidak bisa memuat ini",
    "state.errorBody": "Koneksi terputus di suatu tempat antara sini dan Bali. Coba lagi sebentar.",
    "state.retry": "Coba lagi",
    "state.empty": "Belum ada apa-apa di sini.",
    "state.mapFacade": "Fasad peta — tidak ada skrip pihak ketiga yang dimuat sebelum ini diklik.",

    "lang.label": "Bahasa",
  },
} satisfies Record<Locale, Record<string, string>>;

export type TranslationKey = keyof (typeof dictionary)["en"];
